import { css, html, nothing } from 'lit';
import { t } from 'src/i18n';
import { PageContent } from 'src/pages/page-content.js';
import '../../library/lists/d-list-section-start-tasks.js';
import '../../library/lists/d-list-section.js';
import '../../library/lists/d-list-section-item.js';
import './d-emergency-access.js';
import { customElement, property } from 'lit/decorators.js';
import type { ListSectionInput } from 'src/library/lists/list-section-input.js';
import type {
  HsePlanLink,
  IssueEscalationSection,
  IssuesGroupedListSection,
  OverviewPageViewModel,
  OverviewSection,
  PageLink,
  PageLinks,
  PageTasksListSection,
} from './overview-page-view-model.js';
import '../../content/issues/d-issue-escalation-widget.js';
import type { ItemGroup } from 'src/pages/organization-page/organization-page-view-model.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { features } from '../../store/selectors/features/features';
import { pageIds } from 'src/store/selectors/pages/pageIds';
import { promo } from 'src/store/selectors/promo';
import { userHasModule } from 'src/store/selectors/features/userHasModule';
import type { ListSectionItemInput } from '../../library/lists/listSectionItemInput';
import { uuid } from 'src/utilities/text';
import type { CreateEntityInput } from 'src/layout/parts/create-entity-input';
import type { CreateReportInput } from 'src/layout/parts/create-report-input';
import suggestedDocumentsList from 'src/store/data/suggested-documents.json';
import type { CreateRiskAssessmentInput } from 'src/layout/parts/create-risk-assessment-input';

/**
 *
 * STATUS OK
 */
@customElement('d-overview-page-content')
export class DOverviewPageContent extends PageContent<OverviewPageViewModel> {
  static readonly styles = [
    ...super.styles,
    css`
      d-emergency-access {
        border-top: 1px solid var(--borderColorOnGray);
      }
      .page-link {
        padding: 12px 0 12px var(--listPaddingLeft);
        background-size: 30px 30px;
        background-position: -2px 8px;
        background-repeat: no-repeat;
      }
      d-list-section {
        margin-bottom: 6px;
      }
      d-list-section-start-tasks {
        margin: 12px 0;
      }
    `,
  ];
  @property({ type: Boolean, attribute: 'write-access', reflect: true })
  writeAccess = false;
  @property({ type: Boolean })
  uncoverPageMenu = false;
  @property({ type: Boolean })
  themePage = true;

  private get promoId() {
    if (this.pageView.pageId === pageIds.informationSecurity) {
      return 'data';
    }
    if (this.pageView.pageId === pageIds.hse) {
      return 'hse';
    }
    if (this.pageView.pageId === pageIds.assetManagement) {
      return 'assets';
    }
    return 'complete';
  }

  private get isActivePage() {
    switch (this.pageView.pageId) {
      case pageIds.management:
        return this.pageView.featureStates.pageHse;
      case pageIds.hse:
        return this.pageView.featureStates.pageManagement;
      case pageIds.business:
        return this.pageView.featureStates.pageBusiness;
      case pageIds.assetManagement:
        return this.pageView.featureStates.pageAssetManagement;
      case pageIds.informationSecurity:
        return this.pageView.featureStates.pageInformationSecurity;
      case pageIds.emergency:
        return this.pageView.featureStates.emergency;
      case pageIds.quality:
        return this.pageView.featureStates.pageQuality;
      default:
        return false;
    }
  }

  renderHsePlan(list: HsePlanLink) {
    return html`<a class="hse-plan-link" href="${list.href}">${list.name}</a>`;
  }

  listSectionItems(list: ListSectionInput): ListSectionItemInput[] {
    if (list.items) {
      if (list.icon === 'reports' && this.pageView.pageId === pageIds.hse) {
        return list.items?.map((item) => {
          if (item.suggestion) {
            item.mainIcon = 'suggestion';
            const template = this.pageView.suggestedReports.find((r) => {
              return r.pageId === Number(item.clickData ?? 0);
            });
            if (template) {
              item.clickHandler = () => {
                const id = uuid();
                let formResponsibleFunction: string = '';
                let formResponsibleEmployees: string[] = [];
                let formResponsibleContacts: string[] = [];
                if (this.pageView.defaultFunctionForPage) {
                  formResponsibleFunction = this.pageView.defaultFunctionForPage.uuid;
                  if (this.pageView.defaultFunctionForPage.employees[0] !== this.pageView.currentEmployeeUuid) {
                    if (this.pageView.currentEmployeeUuid) {
                      formResponsibleEmployees = [this.pageView.currentEmployeeUuid];
                    } else if (this.pageView.currentContactUuid) {
                      formResponsibleContacts = [this.pageView.currentContactUuid];
                    }
                  }
                }
                const input: CreateReportInput = {
                  entityType: 'reports',
                  entityUuid: id,
                  pageId: pageIds.hse,
                  templateId: template.mainVersionPageId !== 0 ? template.mainVersionPageId : template.pageId,
                  targetUrl: this.pageView.href + 'reports/' + id + '?edit',
                  name: item.label,
                  content: JSON.stringify({ formContent: template.formContent }),
                  isForm: true,
                  anonymousForm: template.isAnonymousForm,
                  formResponsibleFunction,
                  formResponsibleEmployees: formResponsibleEmployees,
                  formResponsibleContacts: formResponsibleContacts,
                };
                this.dispatchEvent(
                  new CustomEvent<CreateEntityInput>('create-entity', {
                    bubbles: true,
                    composed: true,
                    detail: input,
                  }),
                );
              };
            }
          }
          return item;
        });
      }
      if (list.icon === 'riskAssessments' && this.pageView.pageId === pageIds.emergency) {
        return list.items?.map((item) => {
          if (item.suggestion) {
            item.mainIcon = 'suggestion';
            const template = suggestedDocumentsList.riskAssessments.find((r) => {
              return r.pageId === Number(item.clickData ?? 0);
            });
            if (template) {
              item.clickHandler = () => {
                const id = uuid();
                const input: CreateRiskAssessmentInput = {
                  entityType: 'riskAssessments',
                  entityUuid: id,
                  pageId: pageIds.emergency,
                  templateId: template.mainVersionPageId !== 0 ? template.mainVersionPageId : template.pageId,
                  targetUrl: this.pageView.href + 'riskAssessments/' + id + '?edit',
                  name: item.label,
                };
                this.dispatchEvent(
                  new CustomEvent<CreateEntityInput>('create-entity', {
                    bubbles: true,
                    composed: true,
                    detail: input,
                  }),
                );
              };
            }
          }
          return item;
        });
      }
      return list.items;
    }
    return [];
  }

  onPageLinkClick(event) {
    event.preventDefault();
    if (event.target.hasAttribute('help-page')) {
      const helpPage = event.target.getAttribute('help-page');
      if (helpPage) {
        this.dispatchEvent(
          new CustomEvent<{ page: string }>('update-help', {
            bubbles: true,
            composed: true,
            detail: { page: helpPage },
          }),
        );
      } else {
        this.dispatchEvent(
          new CustomEvent('navigate', {
            bubbles: true,
            composed: true,
            detail: { href: event.target.href },
          }),
        );
      }
    }
  }

  renderListSection(list: ListSectionInput) {
    return html`<d-list-section
      .label=${list.label ?? ''}
      .sublabel=${list.secondaryLabel ?? ''}
      field="${ifDefined(list.field)}"
      .icon=${list.icon}
      .items=${this.listSectionItems(list)}
      .writeAccess=${this.pageView.writeAccess}
      .themePage=${this.themePage}
      .contentStickyTop=${this.contentStickyTop}
      .actions=${list.actions ?? []}
      @action=${(e: CustomEvent) => this.onListAction(e.detail)}
    ></d-list-section>`;
  }

  renderPageLinks(list: PageLinks) {
    return html`
      ${list.items.map((pageLink: PageLink) => {
        return html`<a
          class="page-link"
          style="${this.pageLinkStyle(pageLink.type)}"
          href="${pageLink.href}"
          help-page="${pageLink.helpPage ?? ''}"
          @click=${(e: Event) => this.onPageLinkClick(e)}
          >${pageLink.name}</a
        >`;
      })}
    `;
  }

  renderList(list: OverviewSection) {
    switch (list.type) {
      case 'hse-plan-link':
        return html`${this.renderHsePlan(list)}`;
      case 'page-links':
        return html`${this.renderPageLinks(list)}`;
      case 'start-tasks':
        return html` <d-list-section-start-tasks
          .items=${list.items}
          .showAll=${true}
          .pageName=${this.pageView.name}
        ></d-list-section-start-tasks>`;
      case 'list':
        return this.renderListSection(list);
      case 'page-tasks':
        return this.renderPageTasksListSection(list);
      case 'issues-grouped':
        return this.renderGroupedListSection(list);
      case 'issue-escalation':
        return this.renderIssueEscalation(list);
    }
  }

  renderIssueEscalation(list: IssueEscalationSection) {
    return html`<d-issue-escalation-widget
      .candidates=${list.candidates}
      .enabled=${list.enabled}
      .href=${list.href}
    ></d-issue-escalation-widget>`;
  }

  renderGroupedListSection(list: IssuesGroupedListSection) {
    return html`
      <d-list-section
        theme-page
        .items=${this.asListItem(list.items)}
        icon="issues"
        field="${list.field}"
        organization-id="${this.pageView.organizationId}"
        .writeAccess=${this.writeAccess}
        .contentStickyTop=${this.contentStickyTop}
      >
      </d-list-section>
    `;
  }

  private renderEmergencyAccess() {
    if (this.pageView.pageId === pageIds.emergency) {
      return html`
        <d-emergency-access
          .currentUserHasAccess=${this.pageView.currentUserHarEmergencyAccess}
          .people=${this.pageView.emergencyAccessPeople}
          .accessList=${this.pageView.emergencyAccessList}
        ></d-emergency-access>
      `;
    }
    return nothing;
  }

  renderContent() {
    if (!this.isActivePage) {
      const promoData = promo.find((p) => {
        return p.id === this.promoId;
      });
      if (promoData) {
        const filteredPromoData = {
          ...promoData,
          modules: promoData.modules.filter((m) => !userHasModule(m, this.pageView.featureStates)),
        };
        return html` <d-promo-section
          theme-page
          .employeesCount=${this.pageView.employeesCount}
          .specialTerms=${this.pageView.specialTerms}
          .promoData=${filteredPromoData}
          .features=${features}
          .canUpgrade=${this.writeAccess}
        ></d-promo-section>`;
      }
      return nothing;
    }
    return html`${this.renderEmergencyAccess()} ${this.pageView.lists.map((list) => this.renderList(list))} `;
  }

  private pageLinkStyle(type: string) {
    return 'background-image: url(/images/' + type + '-color.svg)';
  }

  private onListAction(action: string) {
    alert('Unhandled action: ' + action);
  }

  private asListItem(list: ItemGroup[]): ListSectionItemInput[] {
    return list.map((v) => ({
      label: v.label,
      accessible: false,
      sublistToggle: v.filtered,
      sublistHidden: v.filtered,
      items: v.items.map((item) => ({
        label: item.dateDisplay + ' ' + item.label,
        href: item.href,
        mainIcon: item.locked ? 'lock' : undefined,
        accessible: item.accessible,
        hasDraft: item.hasDraft,
      })),
    }));
  }

  private renderPageTasksListSection(list: PageTasksListSection) {
    const m: ListSectionItemInput[] = list.functionsWithTasks.map((i) => ({
      label: i.name,
      secondaryLabel: i.assignedNames,
      href: i.href,
      updateStatus: i.updateStatus,
      hasDraft: i.hasDraft,
      accessible: true,
      limitItems: 5,
      items: i.tasks.map((t) => ({
        label: t.name,
        accessible: true,
        updateStatus: t.updateStatus,
        hasDraft: t.hasDraft,
        href: t.href,
      })),
    }));
    return html`
      <d-list-section
        theme-page
        extra-spacing
        .items=${m}
        icon="functions"
        field="organization_functions"
        .sublabel=${t('overview.andRoutines')}
        organization-id="${this.pageView.organizationId}"
        .writeAccess=${this.writeAccess}
        .contentStickyTop=${this.contentStickyTop}
      >
      </d-list-section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-overview-page-content': DOverviewPageContent;
  }
}
